import {useEffect, useMemo, useState} from 'react';
import {
  Box,
  Button,
  Checkbox,
  Flex,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import {FiCheck, FiSearch, FiSliders, FiUsers} from '../../icons';
import type {
  ScheduleProviderOption,
  ScheduleScopeSelection,
} from './plannerProviderScope';
import {useScrollbarSx} from './usePlannerStyles';

interface ScheduleScope {
  needsManualSelection: boolean;
  autoMatched: boolean;
  availableProviders: ScheduleProviderOption[];
  selection: ScheduleScopeSelection | null;
  isAll: boolean;
  selectedKeys: string[];
  isManual: boolean;
  totalAppointments: number;
  scopedCount: number;
  setSelection: (next: ScheduleScopeSelection | null) => void;
  clearSelection: () => void;
}

interface Props {
  scope: ScheduleScope;
  styles: Record<string, any>;
}

const ALL_KEY = '__all__';

// Human summary for the active scope, e.g. "Dr. Orellana, Jose +2 others".
function selectionSummary(scope: ScheduleScope): string {
  if (scope.isAll) return 'Entire schedule';
  if (!scope.selectedKeys.length) return 'No providers selected';
  const byKey = new Map(scope.availableProviders.map(p => [p.key, p.name]));
  const names = scope.selectedKeys.map(key => byKey.get(key)).filter(Boolean);
  if (!names.length) {
    const count = scope.selectedKeys.length;
    return `${count} provider${count === 1 ? '' : 's'}`;
  }
  const [first, ...rest] = names;
  if (!rest.length) return String(first);
  return `${first} +${rest.length} other${rest.length === 1 ? '' : 's'}`;
}

const ScopeModal = ({
  isOpen,
  onClose,
  scope,
  styles,
}: {
  isOpen: boolean;
  onClose: () => void;
  scope: ScheduleScope;
  styles: Record<string, any>;
}) => {
  const scrollbarSx = useScrollbarSx();
  const [query, setQuery] = useState('');
  const [pendingAll, setPendingAll] = useState(scope.isAll);
  const [pendingKeys, setPendingKeys] = useState<Set<string>>(
    () => new Set(scope.selectedKeys),
  );

  // Re-seed the working selection from the live scope each time the modal
  // opens so it always reflects what's currently applied.
  useEffect(() => {
    if (!isOpen) return;
    setQuery('');
    setPendingAll(scope.isAll);
    setPendingKeys(new Set(scope.selectedKeys));
  }, [isOpen, scope.isAll, scope.selectedKeys]);

  const filteredProviders = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return scope.availableProviders;
    return scope.availableProviders.filter(p =>
      p.name.toLowerCase().includes(q),
    );
  }, [query, scope.availableProviders]);

  const selectedCount = pendingAll
    ? scope.availableProviders.length
    : pendingKeys.size;

  const toggleProvider = (key: string) => {
    setPendingAll(false);
    setPendingKeys(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleAll = () => {
    setPendingAll(prev => {
      const nextAll = !prev;
      if (nextAll) setPendingKeys(new Set());
      return nextAll;
    });
  };

  const handleApply = () => {
    if (pendingAll) {
      scope.setSelection({all: true, keys: []});
    } else if (pendingKeys.size) {
      scope.setSelection({all: false, keys: [...pendingKeys]});
    } else {
      scope.clearSelection();
    }
    onClose();
  };

  const handleClear = () => {
    setPendingAll(false);
    setPendingKeys(new Set());
    scope.clearSelection();
    onClose();
  };

  const canApply = pendingAll || pendingKeys.size > 0;

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent
        bg={styles.rowBg}
        borderRadius="xl"
        boxShadow={styles.cardShadow}
        mx={4}
        maxW={{base: 'calc(100% - 2rem)', md: '32rem'}}
        data-testid="planner-schedule-scope-modal"
      >
        <ModalHeader pb={2}>
          <Text
            as="h2"
            color={styles.textColor}
            fontSize="lg"
            fontWeight="semibold"
            lineHeight="1.3"
          >
            Choose a schedule to view
          </Text>
          <Text
            color={styles.metaTextColor}
            fontSize="sm"
            fontWeight="normal"
            lineHeight="1.45"
            mt={1}
          >
            Select the attending provider(s) whose patients you want to see.
          </Text>
        </ModalHeader>

        <ModalBody px={5} py={0}>
          <InputGroup size="md" mb={3}>
            <InputLeftElement
              pointerEvents="none"
              h="100%"
              w="2.75rem"
              color={styles.metaTextColor}
            >
              <FiSearch size={16} color="currentColor" aria-hidden="true" />
            </InputLeftElement>
            <Input
              value={query}
              onChange={event => setQuery(event.target.value)}
              aria-label="Search providers"
              placeholder="Search providers"
              bg={styles.rowBg}
              borderColor={styles.rowBorderColor}
              borderRadius="lg"
              color={styles.textColor}
              fontSize="md"
              fontWeight="normal"
              minH="44px"
              pl="2.75rem"
              _focus={styles.insetInputFocusStyles}
              _focusVisible={styles.insetInputFocusStyles}
            />
          </InputGroup>

          <Box
            maxH="22rem"
            overflowY="auto"
            mx={-2}
            px={2}
            pb={1}
            sx={scrollbarSx}
          >
            <ScopeRow
              label="Entire schedule"
              caption={`All ${scope.totalAppointments} appointment${
                scope.totalAppointments === 1 ? '' : 's'
              } today`}
              count={null}
              checked={pendingAll}
              onToggle={toggleAll}
              styles={styles}
              testId={`planner-scope-option-${ALL_KEY}`}
            />

            <Box h="1px" bg={styles.listRowDividerColor} my={1.5} />

            {filteredProviders.length === 0 ? (
              <Flex
                align="center"
                justify="center"
                py={8}
                color={styles.metaTextColor}
                fontSize="sm"
              >
                No providers match “{query.trim()}”.
              </Flex>
            ) : (
              filteredProviders.map(provider => (
                <ScopeRow
                  key={provider.key}
                  label={provider.name}
                  count={provider.count}
                  checked={pendingAll || pendingKeys.has(provider.key)}
                  dimmed={pendingAll}
                  onToggle={() => toggleProvider(provider.key)}
                  styles={styles}
                  testId={`planner-scope-option-${provider.key}`}
                />
              ))
            )}
          </Box>
        </ModalBody>

        <ModalFooter
          pt={3}
          gap={3}
          justifyContent="space-between"
          alignItems="center"
        >
          <Text color={styles.metaTextColor} fontSize="sm" fontWeight="normal">
            {selectedCount} selected
          </Text>
          <HStack spacing={2}>
            <Button
              variant="ghost"
              size="sm"
              minH="40px"
              px={3}
              color={styles.subtleText}
              fontWeight="normal"
              onClick={handleClear}
              isDisabled={!scope.isManual && !canApply}
              _hover={{bg: styles.rowHoverBg}}
              _focusVisible={styles.focusOutlineStyles}
            >
              Clear
            </Button>
            <Button
              size="sm"
              minH="40px"
              px={5}
              bg="opalite.600"
              color="white"
              fontWeight="semibold"
              onClick={handleApply}
              isDisabled={!canApply}
              _hover={{bg: 'opalite.700'}}
              _active={{bg: 'opalite.700'}}
              _focusVisible={styles.focusOutlineStyles}
            >
              Done
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

const ScopeRow = ({
  label,
  caption,
  count,
  checked,
  dimmed = false,
  onToggle,
  styles,
  testId,
}: {
  label: string;
  caption?: string;
  count: number | null;
  checked: boolean;
  dimmed?: boolean;
  onToggle: () => void;
  styles: Record<string, any>;
  testId?: string;
}) => (
  <Flex
    as="button"
    type="button"
    role="checkbox"
    aria-checked={checked}
    onClick={onToggle}
    data-testid={testId}
    align="center"
    gap={3}
    w="100%"
    minH="44px"
    px={3}
    py={2}
    borderRadius="lg"
    textAlign="left"
    bg={checked ? styles.listRowSelectedBg : 'transparent'}
    opacity={dimmed && !checked ? 0.55 : 1}
    transition="background-color 120ms ease"
    _hover={{bg: checked ? styles.listRowSelectedBg : styles.rowHoverBg}}
    _focusVisible={styles.focusOutlineStyles}
  >
    <Checkbox
      isChecked={checked}
      pointerEvents="none"
      tabIndex={-1}
      colorScheme="opalite"
      icon={<FiCheck size={12} />}
      aria-hidden="true"
    />
    <Box minW={0} flex={1}>
      <Text
        color={styles.textColor}
        fontSize="md"
        fontWeight={checked ? 'semibold' : 'normal'}
        lineHeight="1.3"
        noOfLines={1}
      >
        {label}
      </Text>
      {caption && (
        <Text
          color={styles.metaTextColor}
          fontSize="xs"
          fontWeight="normal"
          lineHeight="1.3"
          noOfLines={1}
        >
          {caption}
        </Text>
      )}
    </Box>
    {count != null && (
      <Box
        flexShrink={0}
        bg={styles.countBg}
        color={styles.countColor}
        borderRadius="md"
        px={2.5}
        py={0.5}
        minW="2.25rem"
        textAlign="center"
        fontSize="sm"
        fontWeight="normal"
        lineHeight="1.4"
      >
        {count}
      </Box>
    )}
  </Flex>
);

const PlannerScheduleScope = ({scope, styles}: Props) => {
  const {isOpen, onOpen, onClose} = useDisclosure();

  if (!scope) return null;

  const showPrompt = scope.needsManualSelection;
  const showBar = !showPrompt && scope.isManual;

  if (!showPrompt && !showBar) return null;

  return (
    <>
      {showPrompt ? (
        <Flex
          data-testid="planner-schedule-scope-prompt"
          direction="column"
          align="center"
          justify="center"
          textAlign="center"
          gap={{base: 3, lg: 4}}
          py={{base: 6, lg: 10}}
          px={4}
        >
          <Flex
            align="center"
            justify="center"
            boxSize="56px"
            borderRadius="full"
            bg={styles.listRowSelectedBg}
            color="opalite.600"
          >
            <FiUsers size={26} aria-hidden="true" />
          </Flex>
          <Box>
            <Text
              as="h3"
              color={styles.textColor}
              fontSize="lg"
              fontWeight="semibold"
              lineHeight="1.3"
            >
              Choose a schedule to view
            </Text>
            <Text
              color={styles.metaTextColor}
              fontSize="md"
              fontWeight="normal"
              lineHeight="1.45"
              mt={1.5}
              maxW="22rem"
              mx="auto"
            >
              We couldn&rsquo;t match you to a provider&rsquo;s schedule. Pick
              the attending(s) whose patients you want to see.
            </Text>
          </Box>
          <Button
            leftIcon={<FiUsers size={16} aria-hidden="true" />}
            size="md"
            minH="44px"
            px={5}
            bg="opalite.600"
            color="white"
            fontWeight="semibold"
            borderRadius="lg"
            onClick={onOpen}
            _hover={{bg: 'opalite.700'}}
            _active={{bg: 'opalite.700'}}
            _focusVisible={styles.focusOutlineStyles}
          >
            Choose providers
          </Button>
        </Flex>
      ) : (
        <Flex
          data-testid="planner-schedule-scope-bar"
          align="center"
          justify="space-between"
          gap={2}
          w="100%"
          minW={0}
          mb={1}
          px={3}
          py={2}
          borderWidth="1px"
          borderColor={styles.rowBorderColor}
          borderRadius="lg"
          bg={styles.rowBg}
        >
          <HStack spacing={2} minW={0} color={styles.subtleText}>
            <Box flexShrink={0} color="opalite.600" display="flex">
              <FiUsers size={16} aria-hidden="true" />
            </Box>
            <Box minW={0}>
              <Text
                color={styles.metaTextColor}
                fontSize="xs"
                fontWeight="normal"
                lineHeight="1.2"
              >
                Viewing
              </Text>
              <Text
                color={styles.textColor}
                fontSize="sm"
                fontWeight="semibold"
                lineHeight="1.3"
                noOfLines={1}
              >
                {selectionSummary(scope)}
              </Text>
            </Box>
          </HStack>
          <Button
            leftIcon={<FiSliders size={14} aria-hidden="true" />}
            size="sm"
            minH="36px"
            px={3}
            flexShrink={0}
            variant="ghost"
            color="opalite.600"
            fontWeight="semibold"
            onClick={onOpen}
            _hover={{bg: styles.rowHoverBg}}
            _focusVisible={styles.focusOutlineStyles}
          >
            Change
          </Button>
        </Flex>
      )}

      <ScopeModal
        isOpen={isOpen}
        onClose={onClose}
        scope={scope}
        styles={styles}
      />
    </>
  );
};

export default PlannerScheduleScope;
